/* eslint-disable react/prop-types */
import React, { useContext } from 'react';
import { useHistory } from 'react-router';
import {
  RecommendationDto,
  RemediationActivityTypeDto
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import {
  ELSButton,
  ELSDataTable
} from '../../components/els.components';
import { StatusPill } from '../../components/status-pill/StatusPill.component';
import { StatusDisplayMap } from '../../constants/remediation.constants';
import { ContentTypeDisplay } from '../../components/content-type-display/ContentTypeDisplay.component';
import {
  ActivityProgressApplyScoreFilterDisplayName,
  EmptyValueDisplay
} from './remediation-student-activity.constants';
import {
  ActivityProgressApplyScoreFilter
} from './remediation-student-activity.models';
import {
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import { AppContext } from '../../components/app/AppProvider.component';
import { RoutePath } from '../../components/app/app.constants';
import {
  getAttemptToViewByFilter,
  getRecommendationTimeSpentDisplay,
  getScoreByFilterApply,
  isAnyRecommendationScored,
  showAttemptViewButton
} from './remediation-student-activity.utilities';
import {
  getRecommendationStatusWithDueDate,
  handleNavigateToContentPerformance,
} from '../../utilities/remediation.utilities';
import { TimeEstimateDisplay } from '../../components/time-estimate-display/TimeEstimateDisplay.component';
import {
  trackAdobeAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';

export type RecommendationDetailTableProps = {
  activityType: RemediationActivityTypeDto;
  selectedApplyScoreFilter: ActivityProgressApplyScoreFilter;
  goalRecommendations: RecommendationDto[];
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const RecommendationDetailTable = (props: RecommendationDetailTableProps): JSX.Element => {
  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const baseState = appState.remediationBaseState;
  const history = useHistory();

  if (!baseState || !baseState.eolsAssessment) {
    return null;
  }

  const {
    activityType
  } = props;

  const getActivityGoalHeader = (): string => {
    if (activityType === RemediationActivityTypeDto.REVIEW_BY_TAXON) {
      return 'Activity Name';
    }

    if (activityType === RemediationActivityTypeDto.APPLY_BY_CONTENT_TYPE) {
      return 'Case Study';
    }

    return '';
  };

  const getContentTypeDisplay = (recommendation: RecommendationDto) => {
    return (
      <ContentTypeDisplay contentType={recommendation.contentType}/>
    );
  };

  const getTimeEstimatedDisplay = (recommendation: RecommendationDto) => {
    return <TimeEstimateDisplay timeEstimateDuration={recommendation.estimatedTime}/>;
  };

  const handleViewClick = (recommendation: RecommendationDto) => {
    const recommendationAttempt = getAttemptToViewByFilter({
      recommendation,
      recommendationAttempts: baseState.recommendationAttempts,
      attemptAssessmentSubmissions: baseState.attemptAssessmentSubmissions,
      attemptFilter: props.selectedApplyScoreFilter
    });

    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:view:${recommendation.assessmentGoalType}`,
        }
      }
    });

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.CONTENT_VIEW_CLICKED,
        props: {
          recommendationId: recommendation.id,
          recommendationAttemptId: recommendationAttempt?.id,
          contentType: recommendation.contentType,
          viewContext: 'activityTypeModal',
          studentUserId: baseState.raasAssessment?.userId,
          remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
          assessmentId: baseState.eolsAssessment?.id
        }
      }
    });

    handleNavigateToContentPerformance({
      appState,
      appStateRef,
      setAppState,
      recommendation,
      recommendationAttempt,
      quizSession: null,
      history,
      ref: RoutePath.REMEDIATION_STUDENT_DETAIL,
      assessment: baseState.eolsAssessment,
    });
  };

  const getStudentViewActivityBtn = (recommendation: RecommendationDto) => {
    if (!showAttemptViewButton({
      recommendation,
      recommendationAttempts: baseState.recommendationAttempts,
      attemptAssessmentSubmissions: baseState.attemptAssessmentSubmissions,
      attemptFilter: props.selectedApplyScoreFilter,
    })) {
      return ' ';
    }

    return (
      <ELSButton
        type={ELSButtonType.SECONDARY}
        size={ELSButtonSize.SMALL}
        onClick={() => handleViewClick(recommendation)}
      >
        View
      </ELSButton>
    );
  };

  const getStatusDisplay = (recommendation: RecommendationDto) => {
    if (!recommendation) {
      return null;
    }

    const status = getRecommendationStatusWithDueDate({
      recommendation,
      baseState,
    });

    return (
      <StatusPill status={status}>
        {StatusDisplayMap[status]}
      </StatusPill>
    );
  };

  return (
    <>
      {props.goalRecommendations && (
        <>
          <ELSDataTable
            data={props.goalRecommendations}
            noWrap
            columns={[
              {
                header: getActivityGoalHeader(),
                field: 'title'
              },
              {
                header: 'Status',
                field: 'vtwId',
                customRender: (recommendation: RecommendationDto) => getStatusDisplay(recommendation)
              },
              {
                header: 'Content Details',
                field: 'contentType',
                customRender: (recommendation: RecommendationDto) => getContentTypeDisplay(recommendation)
              },
              ...(isAnyRecommendationScored(props.goalRecommendations) ? [
                {
                  header: ActivityProgressApplyScoreFilterDisplayName[props.selectedApplyScoreFilter],
                  field: 'contentType',
                  customRender: (recommendation: RecommendationDto) => {
                    const score = getScoreByFilterApply({
                      recommendationAttempts: baseState.recommendationAttempts,
                      attemptAssessmentSubmissions: baseState.attemptAssessmentSubmissions,
                      recommendation,
                      attemptFilter: props.selectedApplyScoreFilter
                    });
                    return score
                      ? `${score}%`
                      : EmptyValueDisplay;
                  }
                }
              ] : []),
              {
                header: 'Estimated Time',
                field: 'estimatedTime',
                customRender: (recommendation: RecommendationDto) => getTimeEstimatedDisplay(recommendation)
              },
              {
                header: 'Time Spent',
                field: 'timeSpent',
                customRender: (recommendation: RecommendationDto) => getRecommendationTimeSpentDisplay({
                  recommendation,
                  baseState
                })
              },
              {
                header: ' ',
                customRender: (recommendation: RecommendationDto) => getStudentViewActivityBtn(recommendation)
              }
            ]
            }
          >
          </ELSDataTable>
        </>
      )}
    </>
  );
};

export default RecommendationDetailTable;
